import React from 'react';
import { ZapIcon, VolumeXIcon } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
export function Header() {
  const {
    currentEra
  } = useTheme();
  // Era-specific title styles
  const getTitleStyles = () => {
    switch (currentEra) {
      case '60s':
        return 'text-6xl md:text-8xl font-bold tracking-tighter uppercase text-center font-serif';
      case '70s':
        return 'text-6xl md:text-8xl font-bold tracking-widest uppercase text-center';
      case '80s':
        return 'text-6xl md:text-8xl font-bold tracking-tighter uppercase text-center';
      case '90s':
        return 'text-6xl md:text-8xl font-bold tracking-tighter uppercase text-center';
      case '2000s':
        return 'text-6xl md:text-8xl font-bold tracking-normal uppercase text-center';
      default:
        return 'text-6xl md:text-8xl font-bold tracking-tighter uppercase text-center';
    }
  };
  // Era-specific magazine name
  const getMagazineName = () => {
    switch (currentEra) {
      case '60s':
        return <h1 className={getTitleStyles()} data-id="element-91">
            <span className="text-black" data-id="element-92">RE</span>
            <span className="text-black" data-id="element-93">VOLT</span>
          </h1>;
      case '70s':
        return <h1 className={getTitleStyles()} data-id="element-94">
            <span className="text-amber-800" data-id="element-95">RE</span>
            <span className="text-amber-600" data-id="element-96">VOLT</span>
          </h1>;
      case '80s':
        return <h1 className={getTitleStyles()} style={{
          textShadow: '3px 3px 0 #00ffff, -3px -3px 0 #ff00ff'
        }} data-id="element-97">
            <span className="text-yellow-300" data-id="element-98">RE</span>
            <span className="text-cyan-400" data-id="element-99">VOLT</span>
          </h1>;
      case '90s':
        return <h1 className={getTitleStyles()} data-id="element-100">
            <span className="text-cyan-400" data-id="element-101">RE</span>
            <span className="text-pink-500" data-id="element-102">VOLT</span>
          </h1>;
      case '2000s':
        return <h1 className={getTitleStyles()} style={{
          textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
        }} data-id="element-103">
            <span className="text-blue-600" data-id="element-104">RE</span>
            <span className="text-orange-500" data-id="element-105">VOLT</span>
          </h1>;
      default:
        return <h1 className={getTitleStyles()} data-id="element-106">
            <span className="text-cyan-400" data-id="element-107">RE</span>
            <span className="text-pink-500" data-id="element-108">VOLT</span>
          </h1>;
    }
  };
  // Era-specific subtitle
  const getSubtitle = () => {
    switch (currentEra) {
      case '60s':
        return 'Underground Press';
      case '70s':
        return 'Liberation Journal';
      case '80s':
        return 'Queer Power Press';
      case '90s':
        return 'Queer Power Press';
      case '2000s':
        return 'Digital Queer Media';
      default:
        return 'Queer Power Press';
    }
  };
  // Era-specific issue info styles
  const getIssueInfoStyles = () => {
    switch (currentEra) {
      case '60s':
        return 'mt-6 flex justify-between items-center border-y-2 border-black py-2';
      case '70s':
        return 'mt-6 flex justify-between items-center border-y-2 border-amber-800 py-2';
      case '80s':
        return 'mt-6 flex justify-between items-center border-y-2 border-dashed border-cyan-400 py-2';
      case '90s':
        return 'mt-6 flex justify-between items-center border-y-2 border-dashed border-pink-500 py-2';
      case '2000s':
        return 'mt-6 flex justify-between items-center border-y-2 border-blue-600 py-2';
      default:
        return 'mt-6 flex justify-between items-center border-y-2 border-dashed border-pink-500 py-2';
    }
  };
  // Era-specific geometric elements
  const getGeometricElements = () => {
    switch (currentEra) {
      case '60s':
        return <>
            <div className="absolute -top-4 -left-4 w-32 h-32 bg-black rotate-12 z-0" data-id="element-109"></div>
            <div className="absolute top-12 right-12 w-24 h-24 bg-black rotate-45 z-0" data-id="element-110"></div>
          </>;
      case '70s':
        return <>
            <div className="absolute -top-4 -left-4 w-32 h-32 bg-amber-800 rounded-full z-0" data-id="element-111"></div>
            <div className="absolute top-12 right-12 w-24 h-24 bg-amber-600 rounded-full z-0" data-id="element-112"></div>
          </>;
      case '80s':
        return <>
            <div className="absolute -top-4 -left-4 w-32 h-32 bg-pink-500 rotate-12 z-0" style={{
            clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)'
          }} data-id="element-113"></div>
            <div className="absolute top-12 right-12 w-24 h-24 bg-cyan-400 z-0" style={{
            clipPath: 'polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)'
          }} data-id="element-114"></div>
          </>;
      case '90s':
        return <>
            <div className="absolute -top-4 -left-4 w-32 h-32 bg-pink-500 rotate-12 z-0" data-id="element-115"></div>
            <div className="absolute top-12 right-12 w-24 h-24 bg-yellow-400 rotate-45 z-0" data-id="element-116"></div>
          </>;
      case '2000s':
        return <>
            <div className="absolute -top-4 -left-4 w-32 h-32 bg-gradient-to-r from-blue-600 to-orange-500 rounded-lg shadow-lg z-0" data-id="element-117"></div>
            <div className="absolute top-12 right-12 w-24 h-24 bg-gradient-to-r from-orange-500 to-blue-600 rounded-lg shadow-lg z-0" data-id="element-118"></div>
          </>;
      default:
        return <>
            <div className="absolute -top-4 -left-4 w-32 h-32 bg-pink-500 rotate-12 z-0" data-id="element-119"></div>
            <div className="absolute top-12 right-12 w-24 h-24 bg-yellow-400 rotate-45 z-0" data-id="element-120"></div>
          </>;
    }
  };
  // Get era-specific icon and styling
  const getIconElement = () => {
    switch (currentEra) {
      case '60s':
        return <span className="text-black font-bold" data-id="element-121">✊</span>;
      case '70s':
        return <span className="text-amber-800 font-bold" data-id="element-122">☮</span>;
      case '80s':
        return <ZapIcon className="text-yellow-300 h-6 w-6" data-id="element-123" />;
      case '90s':
        return <ZapIcon className="text-yellow-400 h-6 w-6" data-id="element-124" />;
      case '2000s':
        return <span className="text-orange-500 font-bold" data-id="element-125">@</span>;
      default:
        return <ZapIcon className="text-yellow-400 h-6 w-6" data-id="element-126" />;
    }
  };
  // Get era-specific issue label
  const getIssueLabel = () => {
    switch (currentEra) {
      case '60s':
        return <span className="bg-black text-white px-2 py-1" data-id="element-127">ISSUE 23</span>;
      case '70s':
        return <span className="bg-amber-800 text-white px-2 py-1 rounded-full" data-id="element-128">
            ISSUE 23
          </span>;
      case '80s':
        return <span className="bg-cyan-400 text-black px-2 py-1" style={{
          clipPath: 'polygon(0 0, 100% 0, 90% 100%, 10% 100%)'
        }} data-id="element-129">
            ISSUE 23
          </span>;
      case '90s':
        return <span className="bg-cyan-400 text-black px-2 py-1" data-id="element-130">ISSUE 23</span>;
      case '2000s':
        return <span className="bg-blue-600 text-white px-2 py-1 rounded" data-id="element-131">
            ISSUE 23
          </span>;
      default:
        return <span className="bg-cyan-400 text-black px-2 py-1" data-id="element-132">ISSUE 23</span>;
    }
  };
  // Get era-specific free press label
  const getFreeLabel = () => {
    switch (currentEra) {
      case '60s':
        return <span className="bg-black text-white px-2 py-1" data-id="element-133">FREE PRESS</span>;
      case '70s':
        return <span className="bg-amber-600 text-white px-2 py-1 rounded-full" data-id="element-134">
            FREE PRESS
          </span>;
      case '80s':
        return <span className="bg-pink-500 text-black px-2 py-1" style={{
          clipPath: 'polygon(0 0, 100% 0, 90% 100%, 10% 100%)'
        }} data-id="element-135">
            FREE PRESS
          </span>;
      case '90s':
        return <span className="bg-pink-500 text-black px-2 py-1" data-id="element-136">FREE PRESS</span>;
      case '2000s':
        return <span className="bg-orange-500 text-white px-2 py-1 rounded" data-id="element-137">
            FREE PRESS
          </span>;
      default:
        return <span className="bg-pink-500 text-black px-2 py-1" data-id="element-138">FREE PRESS</span>;
    }
  };
  return <header className="pt-6 pb-4" data-id="element-139">
      <div className="relative" data-id="element-140">
        {/* Geometric background element */}
        {getGeometricElements()}
        {/* Magazine title */}
        <div className="relative z-10" data-id="element-141">
          {getMagazineName()}
          <div className="flex justify-center items-center gap-2 mt-2" data-id="element-142">
            {getIconElement()}
            <p className={`text-lg uppercase tracking-widest font-bold ${currentEra === '60s' ? 'text-black' : currentEra === '70s' ? 'text-amber-800' : currentEra === '80s' ? 'text-yellow-300' : currentEra === '90s' ? 'text-yellow-400' : 'text-orange-500'}`} data-id="element-143">
              {getSubtitle()}
            </p>
            {getIconElement()}
          </div>
        </div>
        {/* Issue info */}
        <div className={getIssueInfoStyles()} data-id="element-144">
          <div className="text-sm font-bold" data-id="element-145">{getIssueLabel()}</div>
          <div className={`text-sm font-bold tracking-wide ${currentEra === '60s' ? 'text-black' : currentEra === '70s' ? 'text-amber-800' : currentEra === '80s' ? 'text-cyan-400' : currentEra === '90s' ? 'text-white' : 'text-blue-600'}`} data-id="element-146">
            SUMMER{' '}
            {currentEra === '60s' ? '1964' : currentEra === '70s' ? '1974' : currentEra === '80s' ? '1984' : currentEra === '90s' ? '1994' : '2004'}
          </div>
          <div className="text-sm font-bold" data-id="element-147">{getFreeLabel()}</div>
        </div>
      </div>
    </header>;
}